import _ from 'lodash';

// Stack-exhaustion errors (FRONTEND-8NJ, 8RB and ~9 sibling issues) arrive with
// a SHORT, non-repeating stack — 8NJ's is ~12 frames ending in lodash `deburr`.
// You cannot exhaust a stack in 12 frames, so the frames Sentry shows are not the
// whole story: either the engine truncated `error.stack`, or Sentry's parser did.
// Both engines phrase it differently but it is the same failure.
//
// This captures what the parsed `exception.values[].stacktrace` cannot answer:
// how many frames the raw string actually held, and whether any frame repeats
// (a genuine runaway recursion, like FRONTEND-8SG's InlineCoordsScan.scan, shows
// one frame hundreds of times; an incidental trip does not).

const MESSAGES = [
  'too much recursion', // SpiderMonkey
  'Maximum call stack size exceeded', // V8 / JavaScriptCore
];

// Cap the raw stack we ship — these can be enormous, and Sentry drops
// oversized events entirely rather than truncating them.
const MAX_RAW_STACK_CHARS = 8000;

export const isStackExhaustion = message => (
  typeof message === 'string' && _.some(MESSAGES, m => message.includes(m))
);

// FRONTEND-8NJ arrives as a linked error: React's "recoverable error" wrapper is
// the originalException and the real exhaustion error rides on its `cause`, so a
// message check on the wrapper alone never fires. Walk the chain; the depth cap
// also guards against cause cycles.
const MAX_CAUSE_DEPTH = 5;

export const findStackExhaustion = (error) => {
  let current = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth += 1) {
    if (isStackExhaustion(current.message)) {
      return current;
    }
    current = current.cause;
  }
  return null;
};

// Strip the trailing `:line:column` so the same function at different call sites
// collapses to one frame — otherwise a recursive cycle looks like N distinct
// frames instead of N repeats of one.
const normalizeFrame = line => _.trim(line).replace(/:\d+:\d+\)?$/, '');

const isFrameLine = line => /^\s*(at\s|.*@)/.test(line);

export const summarizeStack = (stack) => {
  if (typeof stack !== 'string' || stack === '') {
    return {
      frameCount: 0, maxRepeat: 0, distinctFrames: 0, topFrame: null, rawStack: null,
    };
  }

  const frames = _.map(_.filter(_.split(stack, '\n'), isFrameLine), normalizeFrame);
  const counts = _.countBy(frames);
  const topFrame = _.maxBy(_.keys(counts), key => counts[key]) || null;

  return {
    frameCount: frames.length,
    maxRepeat: topFrame ? counts[topFrame] : 0,
    distinctFrames: _.size(counts),
    topFrame,
    rawStack: stack.slice(0, MAX_RAW_STACK_CHARS),
  };
};

export default summarizeStack;
